import { TFormItem } from "formifex";

export type ReportTab = {
	key: string;
	label: string;
	arrayPrefixes?: string[];
};

export const REPORT_TAB_CONFIG: Record<string, ReportTab[]> = {
	audit: [
		{
			key: "report_details",
			label: "Report details",
		},
		{
			key: "findings",
			label: "Findings",
			arrayPrefixes: ["findings"],
		},
	],
	incident_report: [
		{
			key: "report_details",
			label: "Report details",
		},
		{
			key: "potential_risk",
			label: "Potential risk",
			arrayPrefixes: ["potential_risks"],
		},
		{
			key: "cause",
			label: "Cause",
			arrayPrefixes: ["causes"],
		},
		{
			key: "follow_up_actions",
			label: "Follow-up actions",
			arrayPrefixes: ["follow_up_actions"],
		},
	],
	moc: [
		{
			key: "report_details",
			label: "Report details",
		},
		{
			key: "follow_up_actions",
			label: "Follow-up actions",
			arrayPrefixes: ["follow_up_actions"],
		},
	],
	ofi: [
		{
			key: "report_details",
			label: "Report details",
		},
		{
			key: "follow_up_actions",
			label: "Follow-up actions",
			arrayPrefixes: ["follow_up_actions"],
		},
	],
	non_conformance: [
		{
			key: "report_details",
			label: "Report details",
		},
		{
			key: "potential_risk",
			label: "Potential risk",
			arrayPrefixes: ["potential_risks"],
		},
		{
			key: "follow_up_actions",
			label: "Follow-up actions",
			arrayPrefixes: ["follow_up_actions"],
		},
	],
	lesson_learned: [
		{
			key: "report_details",
			label: "Report details",
		},
	],
};

// These class names are written as literal strings so Tailwind's JIT scanner can
// generate them. They are injected into section wrappers at runtime via
// applyTabVisibilityClasses().
export const TAB_VISIBILITY_CLASS: Record<string, string> = {
	report_details: "hidden group-data-[active-tab=report-details]:block",
	findings: "hidden group-data-[active-tab=findings]:block",
	potential_risk: "hidden group-data-[active-tab=potential-risk]:block",
	cause: "hidden group-data-[active-tab=cause]:block",
	follow_up_actions: "hidden group-data-[active-tab=follow-up-actions]:block",
};

export type TabbedFormItem = TFormItem & { tab?: string };

export type TabbedParentItem = TabbedFormItem & {
	children: TFormItem[];
};

function hasChildren(item: TabbedFormItem): item is TabbedParentItem {
	return (
		"children" in item &&
		Array.isArray((item as { children?: unknown }).children)
	);
}

export function sectionWithTab<T extends TFormItem>(
	tab: string,
	item: T
): TabbedFormItem {
	return { ...item, tab } as TabbedFormItem;
}

export function applyTabVisibilityClasses(
	items: TFormItem[] | undefined
): TFormItem[] | undefined {
	if (!items) return undefined;
	return items.map((item) =>
		applyToItem(item as TabbedFormItem)
	) as TFormItem[];
}

function applyToItem(item: TabbedFormItem): TabbedFormItem {
	const tab = item.tab;
	if (item.type === "section" && tab && TAB_VISIBILITY_CLASS[tab]) {
		const existingClass = item.wrapperClassName || "";
		const visibilityClass = TAB_VISIBILITY_CLASS[tab];
		return {
			...item,
			wrapperClassName: existingClass
				? `${existingClass} ${visibilityClass}`
				: visibilityClass,
		};
	}
	if (hasChildren(item)) {
		return {
			...item,
			children: item.children.map((child) =>
				applyToItem(child as TabbedFormItem)
			),
		} as TabbedFormItem;
	}
	return item;
}

export function getTabForFieldPath(
	fieldPath: string,
	tabsConfig: ReportTab[],
	fieldToTabMap: Record<string, string>
): string | null {
	for (const tab of tabsConfig) {
		if (tab.arrayPrefixes) {
			for (const prefix of tab.arrayPrefixes) {
				// Array item paths arrive as "findings[0].x" from useRequiredFields
				// and as "findings.0.x" from antd's validateFields error names.
				if (
					fieldPath === prefix ||
					fieldPath.startsWith(`${prefix}[`) ||
					fieldPath.startsWith(`${prefix}.`)
				) {
					return tab.key;
				}
			}
		}
	}
	return fieldToTabMap[fieldPath] || null;
}

export function buildFieldToTabMap(
	items: TFormItem[] | undefined
): Record<string, string> {
	const map: Record<string, string> = {};

	function walk(
		formItems: TabbedFormItem[] | undefined,
		currentTab: string | null
	) {
		if (!formItems) return;
		for (const item of formItems) {
			const tab = item.type === "section" && item.tab ? item.tab : currentTab;

			if (
				item.type === "section" ||
				item.type === "flex" ||
				item.type === "grid"
			) {
				const children = item.children;
				if (children) walk(children as TabbedFormItem[], tab);
			} else if (tab && item.id) {
				map[item.id] = tab;
				// Custom items may declare a separate `fieldName` for the underlying
				// antd form field — map that too so tab routing works for them.
				const fieldName = (item as { fieldName?: string }).fieldName;
				if (fieldName && fieldName !== item.id) {
					map[fieldName] = tab;
				}
			}
		}
	}

	walk(items as TabbedFormItem[], null);
	return map;
}

export function getReportTabs(reportType: string): ReportTab[] | undefined {
	return REPORT_TAB_CONFIG[reportType];
}

export function getTabDataAttributeValue(tabKey: string): string {
	return tabKey.replace(/_/g, "-");
}
